import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "../lib/api";
import axios from "axios";
import { Anvil, Star, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

function StarPick({ value, onChange, testid }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((i) => (
        <button key={i} type="button" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(0)}
          onClick={() => onChange(i)} data-testid={`${testid}-${i}`}>
          <Star className={`w-8 h-8 transition-colors ${i <= (hover || value) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
        </button>
      ))}
    </div>
  );
}

export default function ReviewSubmit() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [scores, setScores] = useState({ comprehension: 0, prix: 0, delai: 0, qualite: 0 });
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = "Laisser un avis | Fabriko";
    axios.get(`${API}/public/review-invite/${token}`)
      .then((r) => { setInvite(r.data); setName(r.data.client_name || ""); })
      .catch((e) => setError(e.response?.status === 410 ? "Ce lien a déjà été utilisé." : "Lien d'avis invalide ou expiré."));
  }, [token]);

  const submit = async () => {
    if (!name.trim()) return toast.error("Indiquez votre nom");
    if (Object.values(scores).some((v) => v < 1)) return toast.error("Notez chaque critère");
    setSaving(true);
    try {
      await axios.post(`${API}/public/reviews`, { token, client_name: name, comment, ...scores });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-navy text-white">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-sky-400 flex items-center justify-center"><Anvil className="w-5 h-5 text-navy" /></div>
          <span className="font-display font-bold text-lg">Fabriko</span>
        </div>
      </header>

      <main className="flex-1 max-w-2xl w-full mx-auto px-5 py-10">
        {error && <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-600" data-testid="review-error">{error}</div>}

        {!error && !invite && <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div>}

        {!error && invite && done && (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="review-thanks">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
            <h1 className="text-2xl font-display font-bold text-navy mt-4">Merci pour votre avis !</h1>
            <p className="text-slate-500 mt-2">Votre retour aide {invite.company_name} et les autres clients.</p>
          </div>
        )}

        {!error && invite && !done && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="review-form">
            <h1 className="text-2xl font-display font-bold text-navy">Évaluez {invite.company_name}</h1>
            <p className="text-slate-500 mt-1">Notez chaque critère de 1 à 5 étoiles.</p>

            <div className="mt-6 space-y-5">
              {invite.criteria.map((cr) => (
                <div key={cr.key} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <span className="text-sm font-medium text-navy">{cr.label}</span>
                  <StarPick value={scores[cr.key]} onChange={(v) => setScores((s) => ({ ...s, [cr.key]: v }))} testid={`stars-${cr.key}`} />
                </div>
              ))}
            </div>

            <div className="mt-6">
              <label className="text-sm font-medium text-slate-700">Votre nom</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="review-name"
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
            </div>
            <div className="mt-4">
              <label className="text-sm font-medium text-slate-700">Votre commentaire (optionnel)</label>
              <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} data-testid="review-comment"
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
            </div>

            <button onClick={submit} disabled={saving} data-testid="submit-review-btn"
              className="mt-6 w-full py-3 rounded-xl bg-navy text-white font-semibold hover:bg-navy-deep transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Publier mon avis
            </button>
            <p className="text-[11px] text-slate-400 text-center mt-2">Avis vérifié — vous avez été invité par l'artisan.</p>
          </div>
        )}
      </main>
    </div>
  );
}
